import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { Checkbox } from 'record-it-components/input/Checkbox/Checkbox';
import { BufferedTextInput, TextInputType } from 'record-it-components/input/TextInput/TextInput';
import { SingleTabEditDialog, SingleTabEditDialogProps } from 'record-it-components/modal/SingleTabEditDialog/SingleTabEditDialog';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { User } from '../../../classes/entities/User/userTypes';
import { useEntityPropertyChangedCallback } from '../../../hooks/entity/useEntityPropertyChangedCallback';
import { useModifyEntityCallback } from '../../../hooks/entity/useModifyEntityCallback';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
export type EditUserDialogProps = PropsWithoutChildren<Omit<SingleTabEditDialogProps, 'tabTitle'>> & {
  user: User;
};
export const EditUserDialog: FunctionComponent<EditUserDialogProps> = React.memo(props => {
  const $ = _c(32);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let remainingProps;
  let user;
  if ($[2] !== t0) {
    ({
      user,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = remainingProps;
    $[4] = user;
  } else {
    remainingProps = $[3];
    user = $[4];
  }
  const {
    currentUser
  } = useCurrentUser();
  const isCurrentUser = user === currentUser;
  const handlePropertyChanged = useModifyEntityCallback(EntityName.User);
  const {
    t
  } = useTranslation();
  const handleEmailChanged = useEntityPropertyChangedCallback(user, "email", handlePropertyChanged);
  const handleNameChanged = useEntityPropertyChangedCallback(user, "name", handlePropertyChanged);
  const handleAdminChanged = useEntityPropertyChangedCallback(user, "admin", handlePropertyChanged);
  let t1;
  if ($[5] !== t) {
    t1 = t("components.user.EditUserDialog.title");
    $[5] = t;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t) {
    t2 = t("entities.User.email");
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== handleEmailChanged || $[10] !== t2 || $[11] !== user.email) {
    t3 = <FormGroup><BufferedTextInput label={t2} value={user.email} onInput={handleEmailChanged} type={TextInputType.Email} data-testid="EditUserDialog.EmailInput" /></FormGroup>;
    $[9] = handleEmailChanged;
    $[10] = t2;
    $[11] = user.email;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t) {
    t4 = t("entities.User.name");
    $[13] = t;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== handleNameChanged || $[16] !== t4 || $[17] !== user.name) {
    t5 = <FormGroup><BufferedTextInput label={t4} value={user.name} onInput={handleNameChanged} type={TextInputType.Text} data-testid="EditUserDialog.NameInput" /></FormGroup>;
    $[15] = handleNameChanged;
    $[16] = t4;
    $[17] = user.name;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t) {
    t6 = t("components.user.EditUserDialog.admin");
    $[19] = t;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== handleAdminChanged || $[22] !== isCurrentUser || $[23] !== t6 || $[24] !== user.admin) {
    t7 = <FormGroup><Checkbox label={t6} value={user.admin} onValueChanged={handleAdminChanged} disabled={isCurrentUser} data-testid="EditUserDialog.AdminCheckbox" /></FormGroup>;
    $[21] = handleAdminChanged;
    $[22] = isCurrentUser;
    $[23] = t6;
    $[24] = user.admin;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== remainingProps || $[27] !== t1 || $[28] !== t3 || $[29] !== t5 || $[30] !== t7) {
    t8 = <SingleTabEditDialog tabTitle={t1} {...remainingProps}>{t3}{t5}{t7}</SingleTabEditDialog>;
    $[26] = remainingProps;
    $[27] = t1;
    $[28] = t3;
    $[29] = t5;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
});